import { useCallback, useMemo } from 'react';

import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import type { MoTaNguon } from '../nghiep-vu/cai-dat';
import type { BienHieuLuc, DongCidr, PhamViCaiDat, SettingDefinition, TuyenThongBao } from '../types';
import { DON_VI } from '../constants';
import { laTiLe, maDonVi } from '../nghiep-vu/don-vi';
import type { LoiDong } from './useFormCaiDat';

/**
 * Dịch mọi thứ của một tham số ra chữ: nhãn, mô tả, giá trị, nguồn, biên, lỗi.
 *
 * Nhãn lấy theo `label_key` của danh mục (`caiDat.<key>.nhan`). Khoá có dấu
 * chấm (`auth.mfa_required`) được i18next tra đúng nhờ cơ chế tìm khoá phẳng.
 * Danh mục có khoá mới mà catalog chưa khai thì hiện chính tên khoá — vẫn dùng
 * được, và `[i18n] thiếu khoá` kêu ở console lúc dev.
 */
export function useDichCaiDat() {
  const { t, locale } = useT();

  /** Dịch nếu catalog có khoá, không thì `undefined`. */
  const neuCo = useCallback(
    (khoa: string, thamSo?: Record<string, string | number>): string | undefined => {
      const s = t(khoa as KhoaDich, thamSo);
      return s === khoa ? undefined : s;
    },
    [t],
  );

  return useMemo(() => {
    const nhan = (key: string) => neuCo(`caiDat.${key}.nhan`) ?? key;
    const moTa = (key: string) => neuCo(`caiDat.${key}.moTa`);
    const giaTriEnum = (key: string, v: string) => neuCo(`caiDat.${key}.${v}`) ?? v;
    const donVi = (unit: string | null | undefined) => {
      const ma = maDonVi(unit);
      return ma ? t(DON_VI[ma]) : unit ?? '';
    };

    /** Số theo đơn vị: tỉ lệ hiện phần trăm. */
    const so = (def: SettingDefinition, n: number) => {
      if (laTiLe(def)) return `${soVN(Math.round(n * 1000) / 10, locale)}%`;
      const dv = donVi(def.unit);
      return dv ? `${soVN(n, locale)} ${dv}` : soVN(n, locale);
    };

    /** Một giá trị bất kỳ ra một dòng chữ — cho hộp xác nhận, lịch sử, dòng chỉ đọc. */
    const giaTri = (def: SettingDefinition | undefined, v: unknown): string => {
      if (v === null || v === undefined || v === '') {
        return t('caiDat.khongDat');
      }
      if (!def) return typeof v === 'string' ? v : JSON.stringify(v);
      switch (def.type) {
        case 'boolean':
          return t(v ? 'caiDat.bat' : 'caiDat.tat');
        case 'integer':
        case 'number':
          return typeof v === 'number' ? so(def, v) : String(v);
        case 'enum':
          return giaTriEnum(def.key, String(v));
        case 'timestamp':
          return ngayGioVN(String(v), locale);
        case 'cidr_list': {
          const ds = v as DongCidr[];
          return ds.length === 0
            ? t('caiDat.danhSachRong')
            : t('caiDat.soDong', { so: soVN(ds.length, locale) });
        }
        case 'notify_route': {
          const r = v as TuyenThongBao;
          return t('caiDat.tomTatTuyen', {
            kenh: r.channels.map((k) => t(`caiDat.kenh.${k}` as KhoaDich)).join(', ') || KHONG_CO,
            so: soVN(r.recipients.length, locale),
            muc: t(`caiDat.mucDo.${r.min_severity}` as KhoaDich),
          });
        }
        case 'alert_rule_list': {
          const ds = v as unknown[];
          return ds.length === 0
            ? t('caiDat.danhSachRong')
            : t('caiDat.soQuyTac', { so: soVN(ds.length, locale) });
        }
        case 'integer_list':
        case 'string_list':
          return (v as unknown[]).length === 0 ? t('caiDat.danhSachRong') : (v as unknown[]).join(', ');
        default:
          return JSON.stringify(v);
      }
    };

    const tenNguon = (loai: MoTaNguon['loai'], goi: MoTaNguon['goi'], theoNenTang: boolean): string => {
      switch (loai) {
        case 'mac_dinh':
          return t(theoNenTang ? 'caiDat.nguon.theoNenTang' : 'caiDat.nguon.macDinh');
        case 'nen_tang':
          return t(theoNenTang ? 'caiDat.nguon.theoNenTang' : 'caiDat.nguon.nenTang');
        case 'goi':
          return goi
            ? t('caiDat.nguon.theoGoi', { goi: t(`baoMat.goi.${goi}` as KhoaDich) })
            : t('caiDat.nguon.goi');
        case 'tenant':
          return t('caiDat.nguon.tenant');
        case 'dang_sua':
          return t('caiDat.nguon.dangSua');
        case 've_ke_thua':
          return t('caiDat.nguon.veKeThua');
      }
    };

    /**
     * Nhãn nguồn. Ở tầng tenant, dòng mà nền tảng quyết (mức sàn của ADR-0078,
     * hoặc tham số chỉ nền tảng sửa) ghi "Theo nền tảng" thay cho "Mặc định" —
     * với tổ chức, mặc định của danh mục cũng là giá trị nền tảng áp xuống.
     */
    const nguon = (m: MoTaNguon, ngu?: { phamVi: PhamViCaiDat; def?: SettingDefinition }) => {
      const theoNenTang =
        ngu?.phamVi === 'TENANT' &&
        !!ngu.def &&
        (ngu.def.inherited_is_floor === true || !ngu.def.scopes.includes('TENANT'));
      return m.loai === 've_ke_thua' && m.nguon_sau
        ? t('caiDat.nguon.seLay', { nguon: tenNguon(m.nguon_sau, m.goi, theoNenTang) })
        : tenNguon(m.loai, m.goi, theoNenTang);
    };

    /**
     * "Từ 1 đến 50 phút theo gói hiện tại". `san`: biên dưới là mức sàn của
     * nền tảng (ADR-0078) — nói rõ để người dùng biết vì sao không hạ được.
     */
    const bien = (
      def: SettingDefinition,
      b: BienHieuLuc | null,
      theoGoi: boolean,
      san = false,
    ): string | undefined => {
      if (!b) return undefined;
      if (def.type === 'cidr_list' || def.type === 'string_list') {
        return b.max_items ? t('caiDat.bien.toiDaDong', { so: soVN(b.max_items, locale) }) : undefined;
      }
      if (def.type === 'alert_rule_list') {
        return b.max_items ? t('caiDat.bien.toiDaQuyTac', { so: soVN(b.max_items, locale) }) : undefined;
      }
      if (def.type === 'timestamp') {
        const phut = b.max_future_minutes ?? def.bounds?.max_future_minutes;
        return phut ? t('caiDat.bien.toiDaPhut', { so: soVN(phut, locale) }) : undefined;
      }
      if (def.type === 'notify_route') {
        return b.max_items ? t('caiDat.bien.toiDaNguoiNhan', { so: soVN(b.max_items, locale) }) : undefined;
      }
      if (def.type !== 'integer' && def.type !== 'number' && def.type !== 'integer_list') return undefined;
      const coMin = b.min !== null && b.min !== undefined;
      const coMax = b.max !== null && b.max !== undefined;
      const ts = {
        min: coMin ? so(def, b.min!) : '',
        max: coMax ? so(def, b.max!) : '',
        so: soVN(b.max_items ?? 0, locale),
      };
      if (def.type === 'integer_list') {
        return coMin && coMax
          ? t(b.max_items ? 'caiDat.bien.tuDenToiDaPhanTu' : 'caiDat.bien.tuDen', ts)
          : undefined;
      }
      if (coMin && coMax) {
        if (san) return t('caiDat.bien.tuSanDen', ts);
        return t(theoGoi ? 'caiDat.bien.tuDenTheoGoi' : 'caiDat.bien.tuDen', ts);
      }
      if (coMax) return t(theoGoi ? 'caiDat.bien.denTheoGoi' : 'caiDat.bien.den', ts);
      if (coMin) return t('caiDat.bien.tu', ts);
      return undefined;
    };

    /** Câu lỗi của một dòng — nói cách sửa, không chỉ nói sai. */
    const loi = (l: LoiDong, def: SettingDefinition | undefined): string => {
      if (l.kieu === 'may-chu') return l.cau;
      const p = l.loi.thamSo ?? {};
      const dinhSo = (n: string | number | undefined) =>
        def && typeof n === 'number' ? so(def, n) : String(n ?? '');
      // `chi_duoc_siet` của boolean không có mức để nêu: dùng câu riêng.
      if (l.loi.ma === 'chi_duoc_siet' && p.gioiHan === undefined) return t('caiDat.loi.chi_duoc_siet_bat');
      if (l.loi.ma === 'quy_tac_sai') {
        return t('caiDat.loi.quy_tac_sai', {
          dong: soVN(Number(p.dong), locale),
          ten: String(p.ten ?? ''),
          truong: t(`quyTacCanhBao.truong.${String(p.truong)}` as KhoaDich),
        });
      }
      const laSoDem =
        l.loi.ma === 'qua_so_dong' || l.loi.ma === 'qua_so_nguoi_nhan' || l.loi.ma === 'qua_so_quy_tac';
      return t(`caiDat.loi.${l.loi.ma}` as KhoaDich, {
        ...p,
        ...(p.min !== undefined ? { min: dinhSo(p.min) } : {}),
        ...(p.max !== undefined ? { max: laSoDem ? soVN(Number(p.max), locale) : dinhSo(p.max) } : {}),
        ...(p.gioiHan !== undefined ? { gioiHan: dinhSo(p.gioiHan) } : {}),
      });
    };

    return { nhan, moTa, giaTriEnum, donVi, giaTri, nguon, bien, loi, so };
  }, [t, locale, neuCo]);
}
